import React, { useEffect, useRef, useState } from "react";

import {
  formatFractionInput,
  formatPercentageInput,
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isValidPercentageDecimal,
  isWithinBounds,
  queryIsLoading,
  useGetFootprintingCropPrimaryDataById,
  useGetPracticeCrops,
  useMutateFootprintingCropPrimaryData,
  useTranslation,
} from "utils";

import { Card, Flex, Form, FormInstance, InputNumber, LazyLoadImage, PageSpin } from "components";

interface CropGeneralProps {
  readOnly?: boolean;
  primaryDataId?: string;
  farmId: string;
  cropId: string;
  year: number;
}

export const CropGeneralForm = ({ readOnly, primaryDataId, farmId, cropId, year }: CropGeneralProps) => {
  const { t } = useTranslation("footprinting");
  const formRef = useRef<FormInstance>(null);

  const [totalWeightTonsPerHa, setTotalWeightTonsPerHa] = useState<number | null>(null);
  const [preDryingMoistureContentPct, setPreDryingMoistureContentPct] = useState<number | null>(null);
  const [byproductPct, setByproductPct] = useState<number | null>(null);
  const [seedWeightKgPerHa, setSeedWeightKgPerHa] = useState<number | null>(null);
  const [soilPh, setSoilPh] = useState<number | null>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  const { data: crops } = useGetPracticeCrops({ enabled: true });
  const cropInfo = crops?.find((crop) => crop.id === cropId);
  const cropName = cropInfo?.name;
  const cropImagePath = cropInfo?.imageURL
    ? cropInfo?.imageURL?.replace("small", "big")
    : "https://klimpublic.s3.eu-central-1.amazonaws.com/footprinting-crop-images/big/placeholder.webp";

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      cropId,
      farmId,
      year,
      totalWeightTonsPerHa,
      preDryingMoistureContentPct,
      byproductPct,
      seedWeightKgPerHa,
      soilPh,
    },
    primaryDataId
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setTotalWeightTonsPerHa(cropPrimaryDataQuery.data.totalWeightTonsPerHa ?? null);
      setPreDryingMoistureContentPct(cropPrimaryDataQuery.data.preDryingMoistureContentPct ?? null);
      setByproductPct(cropPrimaryDataQuery.data.byproductPct ?? null);
      setSeedWeightKgPerHa(cropPrimaryDataQuery.data.seedWeightKgPerHa ?? null);
      setSoilPh(cropPrimaryDataQuery.data.soilPh ?? null);

      formRef.current?.setFieldsValue({
        totalWeightTonsPerHa: cropPrimaryDataQuery.data.totalWeightTonsPerHa ?? null,
        preDryingMoistureContentPct: formatFractionInput(cropPrimaryDataQuery.data.preDryingMoistureContentPct ?? null),
        byproductPct: formatFractionInput(cropPrimaryDataQuery.data.byproductPct ?? null),
        seedWeightKgPerHa: cropPrimaryDataQuery.data.seedWeightKgPerHa ?? null,
        soilPh: cropPrimaryDataQuery.data.soilPh ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["totalWeightTonsPerHa", "preDryingMoistureContentPct", "byproductPct", "seedWeightKgPerHa", "soilPh"] as const).some(
        (field) => cropPrimaryDataQuery.data?.[field] !== null
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess]);

  const isLoading = queryIsLoading(cropPrimaryDataQuery);

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, readOnly]
  );

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200">{t("cropGeneralForm.title", { cropName })}</h1>
      <Card className="padding-100 margin-bottom-100">
        <Flex className="padding-100" gap="1rem" vertical>
          <LazyLoadImage alt={`${cropName || "Crop"} image`} aria-label={`${cropName || "crop"}-logo-image`} src={cropImagePath} />
        </Flex>
        <Flex className="padding-100" gap="1rem" vertical>
          <p className="paragraph-m margin-0">{t("cropGeneralForm.info")}</p>
        </Flex>
      </Card>
      <Form ref={formRef}>
        <Card dataTestId="footprinting-crop-general-form" className="margin-bottom-100 padding-200">
          <Form.Item className="padding-top-50 padding-bottom-50">
            <InputNumber
              disabled={readOnly}
              name="totalWeightTonsPerHa"
              dataTestId="footprinting-crop-yield-weight-input"
              label={t("cropGeneralForm.question.totalWeightTonsPerHa")}
              tooltip={t("cropGeneralForm.tooltips.totalWeightTonsPerHa")}
              suffix="t/ha"
              value={totalWeightTonsPerHa}
              initialValue={totalWeightTonsPerHa}
              onChange={(v) => setTotalWeightTonsPerHa(v)}
              validateFirst
              rules={[
                { required: true, message: t("cropGeneralForm.error.totalWeightTonsPerHa") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("cropGeneralForm.error.totalWeightTonsPerHa")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>

          <Form.Item className="padding-top-50 padding-bottom-50">
            <InputNumber
              disabled={readOnly}
              name="preDryingMoistureContentPct"
              dataTestId="footprinting-crop-moisture-content-input"
              label={t("cropGeneralForm.question.preDryingMoistureContentPct")}
              tooltip={t("cropGeneralForm.tooltips.preDryingMoistureContentPct")}
              suffix="%"
              value={formatFractionInput(preDryingMoistureContentPct)}
              initialValue={formatFractionInput(preDryingMoistureContentPct)}
              onChange={(v) => setPreDryingMoistureContentPct(formatPercentageInput(v))}
              precision={2}
              validateFirst
              rules={[
                { required: true, message: t("cropGeneralForm.error.preDryingMoistureContentPct") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                      return Promise.reject(new Error(t("cropGeneralForm.error.preDryingMoistureContentPct")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>

          <Form.Item className="padding-top-50 padding-bottom-50">
            <InputNumber
              disabled={readOnly}
              name="byproductPct"
              dataTestId="footprinting-crop-byproduct-input"
              label={t("cropGeneralForm.question.byproductPct")}
              tooltip={t("cropGeneralForm.tooltips.byproductPct")}
              suffix="%"
              value={formatFractionInput(byproductPct)}
              initialValue={formatFractionInput(byproductPct)}
              onChange={(v) => setByproductPct(formatPercentageInput(v))}
              precision={2}
              validateFirst
              rules={[
                { required: true, message: t("cropGeneralForm.error.byproductPct") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidPercentageDecimal(v)) {
                      return Promise.reject(new Error(t("cropGeneralForm.error.byproductPct")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>

          <Form.Item className="padding-top-50 padding-bottom-50">
            <InputNumber
              disabled={readOnly}
              name="seedWeightKgPerHa"
              dataTestId="footprinting-crop-seeds-weight-input"
              label={t("cropGeneralForm.question.seedWeightKgPerHa")}
              tooltip={t("cropGeneralForm.tooltips.seedWeightKgPerHa")}
              suffix="kg/ha"
              value={seedWeightKgPerHa}
              initialValue={seedWeightKgPerHa}
              onChange={(v) => setSeedWeightKgPerHa(v)}
              validateFirst
              rules={[
                { required: true, message: t("cropGeneralForm.error.seedWeightKgPerHa") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                      return Promise.reject(new Error(t("cropGeneralForm.error.seedWeightKgPerHa")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>

          <Form.Item className="padding-top-50 padding-bottom-50">
            <InputNumber
              disabled={readOnly}
              name="soilPh"
              dataTestId="footprinting-crop-soil-ph-input"
              label={t("cropGeneralForm.question.soilPh")}
              tooltip={t("cropGeneralForm.tooltips.soilPh")}
              suffix="pH"
              value={soilPh}
              initialValue={soilPh}
              onChange={(v) => setSoilPh(v)}
              validateFirst
              rules={[
                { required: true, message: t("cropGeneralForm.error.soilPh") },
                { type: "number" },
                {
                  validator: (_: unknown, v: number) => {
                    if (isNullOrUndefined(v) || !isWithinBounds(v, 3, 9)) {
                      return Promise.reject(new Error(t("cropGeneralForm.error.soilPh")));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            />
          </Form.Item>
        </Card>
      </Form>
    </div>
  );
};
